import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { HelpTip } from "@/components/HelpTip";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis
} from "recharts";
import {
  FolderKanban, PiggyBank, Users, AlertTriangle, CheckCircle,
  TrendingUp, RefreshCw, ChevronRight, Clock, BookOpen, Briefcase,
  FileSearch, MapPin, ClipboardList, GitBranch, ArrowRight, Trophy,
  Layers, Shield, Star, Sparkles, Eye, Loader2, ExternalLink, Link2,
} from "lucide-react";
import UpcomingWidget from "@/components/UpcomingWidget";
import { toast } from "sonner";
import PreComiteWidget from "@/components/PreComiteWidget";
import OfferAIWizardModal from "@/components/offer/OfferAIWizardModal";
import AIUsageWidget from "@/components/admin/AIUsageWidget";
import BudgetCoverageWidget from "@/components/admin/BudgetCoverageWidget";
import DGFilter, { useDGFilter, DG_LABELS } from "@/components/DGFilter";
import DGCompareCard from "@/components/DGCompareCard";
import { GitCompare } from "lucide-react";

const COLORS = ["#007934", "#8BC34A", "#FFCA28", "#EF5350", "#00ACC1"];

// ── Animated counter ─────────────────────────────────────────────────────────
const useCounter = (end, duration = 1000) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration]);
  return count;
};

// ── KPI Card ─────────────────────────────────────────────────────────────────
const KPICard = ({ title, value, subtitle, icon: Icon, color, suffix = "", delay = 0, onClick, help, "data-testid": testId }) => {
  const animated = useCounter(typeof value === "number" ? value : 0);
  return (
    <div
      onClick={onClick}
      data-testid={testId}
      className={`group bg-white rounded-2xl border border-gray-100 p-5 relative overflow-hidden transition-all duration-200 ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg hover:border-[#007934]/40" : ""}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={`absolute top-0 right-0 h-full w-1 ${color} opacity-80`} />
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-[0.14em] font-semibold flex items-center gap-1">
            {title}
            {/* Iter315 · G07 — ayuda contextual opcional en KPIs */}
            {help && <span onClick={e => e.stopPropagation()}><HelpTip text={help} /></span>}
          </p>
          <p className="text-[32px] leading-tight font-bold text-gray-900 mt-1.5 font-['Outfit'] tabular-nums">
            {typeof value === "number" ? animated.toLocaleString("es-ES") : value}{suffix}
          </p>
          <p className="text-[13px] text-gray-500 mt-1">{subtitle}</p>
        </div>
        <div className={`p-2.5 rounded-xl transition-transform duration-200 group-hover:scale-110 ${color.replace("bg-", "bg-").replace("-600", "-100").replace("-500", "-100")}`}>
          <Icon size={20} className={color.replace("bg-", "text-")} />
        </div>
      </div>
    </div>
  );
};

// ── Quick action button ───────────────────────────────────────────────────────
const QuickAction = ({ icon: Icon, label, to, color = "#007934", bg = "#E8F5E9", badge }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      className="flex items-center gap-3 p-3.5 bg-white border border-gray-100 rounded-xl hover:border-[#007934]/50 hover:shadow-sm hover:-translate-y-px transition-all duration-200 group text-left w-full"
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110" style={{ background: bg }}>
        <Icon size={17} style={{ color }} />
      </div>
      <span className="text-sm font-medium text-gray-800 group-hover:text-[#007934] transition-colors flex-1">{label}</span>
      {badge && <span className="text-[10px] font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{badge}</span>}
      <ArrowRight size={14} className="text-gray-300 group-hover:text-[#007934] group-hover:translate-x-0.5 transition-all" />
    </button>
  );
};

// ── Greeting ─────────────────────────────────────────────────────────────────
const Greeting = ({ name, role, subtitle }) => {
  const { t, i18n } = useTranslation();
  const hour = new Date().getHours();
  const greeting = hour < 13 ? t("greeting.morning") : hour < 20 ? t("greeting.afternoon") : t("greeting.evening");
  const firstName = (name || "").split(" ")[0];
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "es-ES";
  return (
    <div>
      <h1 className="text-[28px] font-semibold tracking-tight text-gray-900 font-['Outfit']">{greeting}, {firstName}</h1>
      <p className="text-sm text-gray-500 mt-0.5">{subtitle} · {new Date().toLocaleDateString(locale, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// DASHBOARD CFO
// ════════════════════════════════════════════════════════════════════════════
function DashboardCFO({ stats, offers, licitaciones }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { dg, setDg, filterByDG } = useDGFilter(user?.email);
  const [compareMode, setCompareMode] = useState(false);

  const offersF      = filterByDG(offers);
  const licitacionesF = filterByDG(licitaciones);
  const pending  = (offersF || []).filter(o => o.status === "pending_cfo_approval");
  const approved = (offersF || []).filter(o => o.status === "approved");
  const rejected = (offersF || []).filter(o => o.status === "rejected" || o.status === "desestimada");

  const ganadas  = (licitacionesF || []).filter(l => l.estado_crm === "ganada");
  const activas  = (licitacionesF || []).filter(l => !["ganada","perdida","descartada"].includes(l.estado_crm));
  const pipelinePonderado = activas.reduce((acc, l) => acc + ((l.importe_licitacion || 0) * (l.probabilidad_exito || 0) / 100), 0);

  // Iter135 · Comparativa DG lado a lado
  const compareMetrics = compareMode ? (() => {
    const byDG = (list, dgVal) => (list || []).filter(x => (x.direccion_general || "").toUpperCase() === dgVal);
    const licTra = byDG(licitaciones, "TRATAMIENTO");
    const licSer = byDG(licitaciones, "SERVICIOS");
    const offTra = byDG(offers, "TRATAMIENTO");
    const offSer = byDG(offers, "SERVICIOS");
    const activasFn = ls => ls.filter(l => !["ganada","perdida","descartada"].includes(l.estado_crm));
    const pondFn = ls => Math.round(ls.reduce((s, l) => s + ((l.importe_licitacion || 0) * (l.probabilidad_exito || 0) / 100), 0));
    const eurFmt = v => (v ? `${(Number(v)/1000).toFixed(0)}K€` : "0");
    return [
      { key: "activas",  label: "Expedientes activos", tra: activasFn(licTra).length, ser: activasFn(licSer).length },
      { key: "ganadas",  label: "Ganadas",             tra: licTra.filter(l => l.estado_crm === "ganada").length, ser: licSer.filter(l => l.estado_crm === "ganada").length },
      { key: "pipeline", label: "Pipeline ponderado",  tra: pondFn(activasFn(licTra)), ser: pondFn(activasFn(licSer)), formatter: eurFmt },
      { key: "pending",  label: "Pendientes de aprobación", tra: offTra.filter(o => o.status === "pending_cfo_approval").length, ser: offSer.filter(o => o.status === "pending_cfo_approval").length },
      { key: "rejected", label: "Rechazadas / desestimadas", tra: offTra.filter(o => ["rejected","desestimada"].includes(o.status)).length, ser: offSer.filter(o => ["rejected","desestimada"].includes(o.status)).length },
    ];
  })() : [];

  const budget = stats?.budget || {};
  const totalSpent = budget.total_spent || 0;
  const maxTotal = budget.max_total || 460000;
  const budgetPct = Math.round((totalSpent / maxTotal) * 100);

  return (
    <div className="space-y-6" data-testid="cfo-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Greeting name={user?.name} subtitle={t("dashboard.roles.cfo")} />
        <div className="flex items-center gap-3">
          {!compareMode && <DGFilter value={dg} onChange={setDg} size="sm" testidPrefix="cfo-dg-filter"/>}
          <button
            onClick={() => setCompareMode(v => !v)}
            data-testid="cfo-dg-compare-toggle"
            aria-pressed={compareMode}
            title="Comparar KPIs de Residuos vs Servicios Urbanos"
            className={`text-xs rounded-full px-3 py-1.5 border inline-flex items-center gap-1 font-semibold transition-all ${
              compareMode
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-800"
            }`}>
            <GitCompare size={12}/> {compareMode ? "Volver" : "Comparar DG"}
          </button>
          {pending.length > 0 && !compareMode && (
            <button onClick={() => navigate("/offers")}
              className="flex items-center gap-2 bg-amber-100 text-amber-800 text-sm px-4 py-2 rounded-full font-semibold hover:bg-amber-200 transition-all animate-pulse"
              data-testid="cfo-pending-badge">
              <AlertTriangle size={14} /> {pending.length} {pending.length === 1 ? t("offers.title") : t("offers.title")} · {t("dashboard.pendingApproval")}
            </button>
          )}
        </div>
      </div>

      {dg && !compareMode && (
        <div className="text-xs text-slate-500 -mt-3" data-testid="cfo-dg-active-note">
          Filtro activo: <b className="text-slate-700">{DG_LABELS[dg]}</b>. Los KPIs y listados sólo muestran expedientes de esa DG (los sin clasificar se mantienen visibles).
        </div>
      )}

      {compareMode && (
        <DGCompareCard metrics={compareMetrics} onClose={() => setCompareMode(false)}/>
      )}

      {/* KPIs CFO */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger" data-testid="cfo-kpi-grid">
        <KPICard title={t("dashboard.pendingApproval")} value={pending.length} subtitle={t("dashboard.pendingApprovalSub")} icon={AlertTriangle} color="bg-amber-500" onClick={() => navigate("/offers")} />
        <KPICard title={t("dashboard.budgetExecuted")} value={totalSpent} subtitle={`${budgetPct}% de ${(maxTotal/1000).toFixed(0)}K€`} icon={PiggyBank} color="bg-blue-600" suffix="€" onClick={() => navigate("/budget")} />
        <KPICard title={t("dashboard.approvedOffers")} value={approved.length} subtitle={t("dashboard.approvedOffersSub")} icon={CheckCircle} color="bg-green-600" onClick={() => navigate("/offers")} />
        <KPICard title={t("dashboard.weightedPipeline")} value={Math.round(pipelinePonderado)} subtitle={t("dashboard.activeFiles", { n: activas.length })} icon={TrendingUp} color="bg-purple-600" suffix="€" onClick={() => navigate("/licitaciones")}
          help="Suma del presupuesto de cada licitación activa multiplicado por su probabilidad de adjudicación estimada (hit rate histórico por tipo/cliente) — no es el presupuesto total sin ponderar." />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Ofertas pendientes de aprobación */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 font-['Outfit']">{t("dashboard.pendingOffersTitle")}</h3>
            <button onClick={() => navigate("/offers")} className="text-xs text-[#007934] flex items-center gap-1 hover:underline">{t("common.actions")} <ChevronRight size={12}/></button>
          </div>
          {pending.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <CheckCircle size={32} className="text-green-400 mb-3" />
              <p className="text-sm font-medium text-gray-700">{t("delegado.empty.title")}</p>
              <p className="text-xs text-gray-400 mt-1">{t("delegado.empty.hint")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pending.map(o => (
                <div key={o.id} onClick={() => navigate(`/offers/${o.id}`)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-amber-100 bg-amber-50 hover:border-amber-300 cursor-pointer transition-all group"
                  data-testid={`cfo-pending-offer-${o.id}`}>
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Briefcase size={14} className="text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{o.title}</p>
                    <p className="text-xs text-gray-500">{o.municipio || "—"} · {o.deadline ? new Date(o.deadline).toLocaleDateString("es-ES") : t("dashboard.noDate")}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-[#007934]">{((o.total_capex || 0) + (o.total_opex || 0)).toLocaleString("es-ES")}€</p>
                    <p className="text-[10px] text-amber-700 font-semibold">{t("offers.states.pending_cfo").toUpperCase()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Accesos rápidos CFO */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">{t("dashboard.quickActions")}</h3>
          <QuickAction icon={Briefcase}    label={t("layout.offers")}           to="/offers"         />
          <QuickAction icon={PiggyBank}    label={t("layout.budget")}           to="/budget"         />
          <QuickAction icon={TrendingUp}   label={t("layout.executive")}        to="/executive"      />
          <QuickAction icon={FileSearch}   label={t("layout.licitaciones")}     to="/licitaciones" />
          <QuickAction icon={MapPin}       label={t("layout.oraZaragoza")}      to="/ora-zaragoza"   />

          {/* Pipeline summary */}
          <div className="bg-[#E8F5E9] rounded-xl p-4 mt-2">
            <p className="text-xs font-bold text-[#007934] uppercase tracking-wide mb-2">Pipeline comercial</p>
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs"><span className="text-gray-600">Expedientes activos</span><span className="font-bold text-gray-800">{activas.length}</span></div>
              <div className="flex justify-between text-xs"><span className="text-gray-600">Ganadas</span><span className="font-bold text-green-700">{ganadas.length}</span></div>
              <div className="flex justify-between text-xs"><span className="text-gray-600">Pipeline ponderado</span><span className="font-bold text-[#007934]">{pipelinePonderado.toLocaleString("es-ES", {maximumFractionDigits:0})}€</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* iter 127: Bandeja prioritaria CFO (offers ≥ 5 M€) */}
      <CFOPriorityQueueCard />

      {/* iter 128: CFO Priority Digest email (daily 07:30 UTC) */}
      <CFODigestBar />

      {/* iter 130: CFO Reject Categories Report (mensual) */}
      <CFORejectReport />

      {/* Upcoming deadlines widget */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <UpcomingWidget />
        <PreComiteWidget />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Iter 127 · CFO Bandeja Prioritaria
// Ofertas ≥ threshold_eur (default 5 M€) pendientes de firma CFO.
// Colores por antigüedad + botón "Recordatorio" que dispara email.
// ════════════════════════════════════════════════════════════════════════════
function CFOPriorityQueueCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remindingId, setRemindingId] = useState(null);
  const [threshold, setThreshold] = useState(5_000_000);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/offers/cfo-priority-queue?threshold_eur=${threshold}`)
      .then(r => setData(r.data))
      .catch(() => setData({ items: [], counts_by_urgency: { stale: 0, warn: 0, fresh: 0 } }))
      .finally(() => setLoading(false));
  }, [threshold]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return null;

  const items = data?.items || [];
  const counts = data?.counts_by_urgency || { stale: 0, warn: 0, fresh: 0 };
  const totalMag = data?.total_magnitud_eur || 0;

  const remind = async (id) => {
    setRemindingId(id);
    try {
      const r = await axios.post(`${API}/offers/${id}/cfo-remind`);
      window.alert(`Recordatorio enviado a ${r.data.sent}/${r.data.cfo_count} CFO`);
    } catch (e) {
      window.alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setRemindingId(null);
    }
  };

  const urgencyStyles = {
    stale: { bg: "bg-rose-50",    border: "border-rose-300",   chip: "bg-rose-500 text-white animate-pulse", label: "> 7 días" },
    warn:  { bg: "bg-amber-50",   border: "border-amber-300",  chip: "bg-amber-500 text-white",              label: "3-7 días" },
    fresh: { bg: "bg-emerald-50", border: "border-emerald-200", chip: "bg-emerald-500 text-white",           label: "< 3 días" },
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="cfo-priority-queue">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-semibold text-gray-900 font-['Outfit'] flex items-center gap-2">
            <Trophy size={16} className="text-amber-600"/>
            Bandeja prioritaria CFO
            {items.length > 0 && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold" data-testid="cfo-pq-count">
                {items.length}
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Ofertas ≥ {(threshold / 1_000_000).toFixed(1)} M€ (CAPEX + OPEX × años) esperando tu firma.
            Total pendiente: <b>{(totalMag / 1_000_000).toFixed(1)} M€</b>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={threshold}
            onChange={e => setThreshold(Number(e.target.value))}
            className="text-xs border border-gray-200 rounded px-2 py-1"
            data-testid="cfo-pq-threshold">
            <option value={1_000_000}>≥ 1 M€</option>
            <option value={3_000_000}>≥ 3 M€</option>
            <option value={5_000_000}>≥ 5 M€</option>
            <option value={10_000_000}>≥ 10 M€</option>
          </select>
          <div className="flex items-center gap-1 text-[10px]">
            {counts.stale > 0 && <span className="bg-rose-500 text-white px-1.5 py-0.5 rounded-full font-bold animate-pulse">{counts.stale} · &gt;7d</span>}
            {counts.warn > 0  && <span className="bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-bold">{counts.warn} · 3-7d</span>}
            {counts.fresh > 0 && <span className="bg-emerald-500 text-white px-1.5 py-0.5 rounded-full font-bold">{counts.fresh} · &lt;3d</span>}
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          <CheckCircle size={26} className="mx-auto text-emerald-400 mb-2"/>
          Nada prioritario pendiente. Buen momento para revisar KPIs YTD.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(it => {
            const s = urgencyStyles[it.urgency];
            return (
              <div
                key={it.id}
                data-testid={`cfo-pq-item-${it.id}`}
                className={`flex items-center gap-3 p-3 rounded-lg border ${s.border} ${s.bg}`}>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.chip}`}>
                  {s.label}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{it.title || "—"}</div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {it.municipality || "—"} · CAPEX {(it.capex_total / 1_000_000).toFixed(2)} M€ ·
                    OPEX {(it.opex_total / 1_000_000).toFixed(2)} M€/año · {it.duracion_anios} años ·
                    esperando desde hace <b>{it.age_days}d</b>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-[#007934] tabular-nums">
                    {(it.magnitud_eur / 1_000_000).toFixed(1)} M€
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/offers/${it.id}`)}
                  className="text-[11px] text-[#007934] hover:underline flex items-center gap-0.5">
                  Abrir <ChevronRight size={11}/>
                </button>
                <button
                  onClick={() => remind(it.id)}
                  disabled={remindingId === it.id}
                  data-testid={`cfo-pq-remind-${it.id}`}
                  className="text-[10px] border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 px-2 py-1 rounded flex items-center gap-1 font-semibold disabled:opacity-50">
                  {remindingId === it.id ? "…" : "Nudge"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter 128 · CFO Priority Digest bar (email diario 07:30 UTC)
// ═══════════════════════════════════════════════════════════════════════════
function CFODigestBar() {
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [threshold, setThreshold] = useState(5_000_000);

  const doPreview = async () => {
    try {
      const r = await axios.get(`${API}/offers/cfo-digest/preview?threshold_eur=${threshold}`);
      setPreviewHtml(r.data.html);
    } catch (e) {
      window.alert("Preview error: " + (e.response?.data?.detail || e.message));
    }
  };
  const doSend = async (all) => {
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/offers/cfo-digest/send-now?threshold_eur=${threshold}&to_self_only=${!all}`
      );
      if (r.data.skipped) window.alert("Cola vacía — no se envió email");
      else if (r.data.mode === "self") window.alert(`Enviado a tu correo (${r.data.count} ofertas)`);
      else window.alert(`Enviado a ${r.data.sent} destinatarios (${r.data.count} ofertas)`);
    } catch (e) {
      window.alert("Send error: " + (e.response?.data?.detail || e.message));
    } finally { setSending(false); }
  };

  return (
    <>
      <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="cfo-digest-bar">
        <Sparkles size={14} className="text-amber-700"/>
        <span className="text-xs font-semibold text-amber-900">CFO Priority Digest</span>
        <span className="text-[11px] text-slate-500 flex-1">
          Email diario 07:30 UTC a Guillermo + Susana + Pablo + admins con la cola pendiente ≥ threshold.
        </span>
        <select
          value={threshold}
          onChange={e => setThreshold(Number(e.target.value))}
          className="text-xs border border-gray-200 rounded px-2 py-1"
          data-testid="cfo-digest-threshold">
          <option value={1_000_000}>≥ 1 M€</option>
          <option value={3_000_000}>≥ 3 M€</option>
          <option value={5_000_000}>≥ 5 M€</option>
          <option value={10_000_000}>≥ 10 M€</option>
        </select>
        <button
          onClick={doPreview}
          data-testid="cfo-digest-preview-btn"
          className="text-xs border border-gray-200 bg-white hover:bg-gray-50 px-3 py-1.5 rounded-lg flex items-center gap-1">
          <Eye size={12}/> Preview
        </button>
        <button
          onClick={() => doSend(false)}
          disabled={sending}
          data-testid="cfo-digest-send-self-btn"
          className="text-xs border border-amber-600 text-amber-800 bg-white hover:bg-amber-100 px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50">
          {sending ? <Loader2 className="animate-spin" size={12}/> : <ArrowRight size={12}/>} Enviarme a mí
        </button>
        <button
          onClick={() => doSend(true)}
          disabled={sending}
          data-testid="cfo-digest-send-all-btn"
          className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50">
          {sending ? <Loader2 className="animate-spin" size={12}/> : <ArrowRight size={12}/>} Enviar a todos
        </button>
      </div>
      {previewHtml && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreviewHtml(null)} data-testid="cfo-digest-preview-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="text-sm font-bold flex items-center gap-2"><Eye size={14}/> Preview · CFO Digest</h3>
              <button onClick={() => setPreviewHtml(null)} className="text-gray-400 hover:text-gray-700">✕</button>
            </div>
            <iframe title="cfo-preview" srcDoc={previewHtml} className="w-full flex-1 border-0" style={{ minHeight: "60vh" }}/>
          </div>
        </div>
      )}
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Iter 130 · CFO Reject Categories Report — patrón mensual de rechazos
// ═══════════════════════════════════════════════════════════════════════════
const CATEGORY_LABELS = {
  over_budget:   { label: "Fuera de presupuesto", color: "#e11d48" },
  wrong_sector:  { label: "Sector no estratégico", color: "#f59e0b" },
  no_capex:      { label: "Falta CAPEX",           color: "#7c3aed" },
  low_margin:    { label: "Margen insuficiente",   color: "#dc2626" },
  risk_juridico: { label: "Riesgo jurídico",       color: "#0891b2" },
  other:         { label: "Otro",                  color: "#64748b" },
  unspecified:   { label: "Sin categoría",         color: "#94a3b8" },
};

// ══════════════════════════════════════════════════════════════════════════
// Iter136 · Botón "Comprobar alerta de patrones de rechazo"
// ══════════════════════════════════════════════════════════════════════════
function RejectPatternAlertBtn() {
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);

  const check = async (send) => {
    setRunning(true);
    try {
      const params = send ? "?force=true" : "?dry_run=true";
      const r = await axios.post(`${API}/offers/reject-patterns/alert-check${params}`);
      const d = r.data;
      if (send) {
        if (d.status === "sent") toast.success(`Alerta enviada a Pablo · ${d.notified?.length || 0} categoría(s)`);
        else if (d.status === "no-op") toast.info("Sin patrones activos (≥ 3 rechazos / 30 d)");
        else toast.error(`Error: ${d.status}`);
      } else {
        setPreview(d);
      }
    } catch (e) { toast.error("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setRunning(false); }
  };

  return (
    <div className="relative">
      <button
        onClick={() => check(false)}
        disabled={running}
        data-testid="reject-pattern-check-btn"
        title="Revisar patrones ≥ 3 rechazos en 30 días (sin enviar)"
        className="text-xs border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-800 rounded-lg px-2.5 py-1 font-semibold flex items-center gap-1 disabled:opacity-50">
        <AlertTriangle size={11}/> Revisar patrones
      </button>
      {preview && (
        <div className="absolute right-0 top-8 z-20 bg-white border border-rose-200 shadow-xl rounded-xl p-3 w-80" data-testid="reject-pattern-preview">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-rose-800 text-xs">Vista previa alerta</span>
            <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-700 text-xs">✕</button>
          </div>
          {preview.patterns_detected === 0 ? (
            <div className="text-[11px] text-emerald-700">Sin categorías con ≥ 3 rechazos en 30 días.</div>
          ) : (
            <>
              <div className="text-[11px] text-slate-600 mb-2">
                <b>{preview.patterns_detected}</b> patrón(es) detectado(s).
                {preview.skipped_cooldown?.length > 0 && (
                  <span className="block text-slate-400 mt-1">
                    Omitidos por cooldown 7d: {preview.skipped_cooldown.join(", ")}
                  </span>
                )}
              </div>
              {preview.would_notify?.length > 0 && (
                <div className="text-[11px] mb-2">
                  Se notificarían: <b className="text-rose-800">{preview.would_notify.join(", ")}</b>
                </div>
              )}
              <button
                onClick={() => { setPreview(null); check(true); }}
                data-testid="reject-pattern-send-btn"
                className="w-full bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 flex items-center justify-center gap-1">
                Enviar alerta a Pablo ahora (forzar)
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CFORejectReport() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(3);

  useEffect(() => {
    axios.get(`${API}/offers/cfo-reject-report?months=${months}`)
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, [months]);

  if (!data) return null;
  if (data.total === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="cfo-reject-report">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900 font-['Outfit'] flex items-center gap-2">
            <AlertTriangle size={16} className="text-slate-400"/>
            Rechazos CFO · patrón
          </h3>
          <select value={months} onChange={e => setMonths(Number(e.target.value))} className="text-xs border rounded px-2 py-1" data-testid="cfo-reject-months">
            <option value={1}>1 mes</option>
            <option value={3}>3 meses</option>
            <option value={6}>6 meses</option>
            <option value={12}>12 meses</option>
          </select>
        </div>
        <div className="text-center py-6 text-gray-400 text-sm">
          <CheckCircle size={22} className="mx-auto text-emerald-400 mb-2"/>
          Sin rechazos en los últimos {months} {months === 1 ? "mes" : "meses"}.
        </div>
      </div>
    );
  }

  const maxCount = Math.max(...data.by_category.map(c => c.count), 1);
  const total = data.total;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="cfo-reject-report">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-semibold text-gray-900 font-['Outfit'] flex items-center gap-2">
          <AlertTriangle size={16} className="text-rose-600"/>
          Rechazos CFO · patrón por categoría
          <span className="text-xs bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full font-bold" data-testid="cfo-reject-total">
            {total} rechazos
          </span>
        </h3>
        <div className="flex items-center gap-2">
          <RejectPatternAlertBtn/>
          <select value={months} onChange={e => setMonths(Number(e.target.value))} className="text-xs border rounded px-2 py-1" data-testid="cfo-reject-months">
            <option value={1}>1 mes</option>
            <option value={3}>3 meses</option>
            <option value={6}>6 meses</option>
            <option value={12}>12 meses</option>
          </select>
        </div>
      </div>

      {/* Bar chart categoría */}
      <div className="space-y-2 mb-4">
        {data.by_category.map(c => {
          const meta = CATEGORY_LABELS[c.category] || CATEGORY_LABELS.unspecified;
          const pct = (c.count / maxCount) * 100;
          return (
            <div key={c.category} className="flex items-center gap-3 text-xs" data-testid={`cfo-reject-cat-${c.category}`}>
              <div className="w-40 text-slate-700 font-semibold truncate" title={meta.label}>{meta.label}</div>
              <div className="flex-1 bg-slate-50 rounded-full h-6 relative overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, background: meta.color }}/>
                <span className="absolute inset-y-0 right-2 text-[10px] font-bold flex items-center text-slate-700">
                  {c.count} · {c.total_mm.toFixed(1)} M€
                </span>
              </div>
              <div className="w-12 text-right font-mono text-[10px] text-slate-500">
                {((c.count / total) * 100).toFixed(0)}%
              </div>
            </div>
          );
        })}
      </div>

      {/* Ejemplos recientes */}
      {data.recent_rejections.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Últimos rechazos</p>
          <div className="space-y-1">
            {data.recent_rejections.map((r) => {
              const meta = CATEGORY_LABELS[r.category] || CATEGORY_LABELS.unspecified;
              return (
                <div key={r.id} className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1" data-testid={`cfo-reject-recent-${r.id}`}>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: meta.color + "22", color: meta.color }}>
                    {meta.label}
                  </span>
                  <span className="flex-1 truncate text-slate-700">{r.title}</span>
                  <span className="text-[10px] text-slate-500">{(r.rejected_at || "").slice(0, 10)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
function DashboardOfferManager({ stats, offers, licitaciones }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { dg, setDg, filterByDG } = useDGFilter(user?.email);
  const [compareMode, setCompareMode] = useState(false);

  const PIPELINE_COLS = [
    { key: "prospecto",       label: t("dashboard.pipeline.prospecto"),  color: "bg-gray-100 text-gray-700"    },
    { key: "recogiendo_docs", label: t("dashboard.pipeline.docs"),       color: "bg-blue-100 text-blue-700"    },
    { key: "analizando",      label: t("dashboard.pipeline.aiAnalysis"), color: "bg-violet-100 text-violet-700"},
    { key: "prep_oferta",     label: t("dashboard.pipeline.prep"),       color: "bg-amber-100 text-amber-700"  },
    { key: "presentada",      label: t("dashboard.pipeline.submitted"),  color: "bg-orange-100 text-orange-700"},
    { key: "negociacion",     label: t("dashboard.pipeline.negotiation"),color: "bg-cyan-100 text-cyan-700"    },
  ];

  const licitacionesF = filterByDG(licitaciones);
  const offersF       = filterByDG(offers);
  const pipelineMap = PIPELINE_COLS.reduce((acc, col) => {
    acc[col.key] = (licitacionesF || []).filter(l => l.estado_crm === col.key);
    return acc;
  }, {});

  const ganadas = (licitacionesF || []).filter(l => l.estado_crm === "ganada");
  const totalActive = PIPELINE_COLS.reduce((acc, c) => acc + pipelineMap[c.key].length, 0);
  const myOffers = (offersF || []).filter(o => !["approved","desestimada","rejected"].includes(o.status));

  return (
    <div className="space-y-6" data-testid="offer-manager-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Greeting name={user?.name} subtitle={t("dashboard.roles.offerManager")} />
        <div className="flex items-center gap-3">
          {!compareMode && <DGFilter value={dg} onChange={setDg} size="sm" testidPrefix="om-dg-filter"/>}
          <button
            onClick={() => setCompareMode(v => !v)}
            data-testid="om-dg-compare-toggle"
            aria-pressed={compareMode}
            title="Comparar KPIs de Residuos vs Servicios Urbanos"
            className={`text-xs rounded-full px-3 py-1.5 border inline-flex items-center gap-1 font-semibold transition-all ${
              compareMode
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-800"
            }`}>
            <GitCompare size={12}/> {compareMode ? "Volver" : "Comparar DG"}
          </button>
          {!compareMode && (
            <button onClick={() => navigate("/licitaciones")}
              className="flex items-center gap-2 bg-[#007934] text-white text-sm px-4 py-2 rounded-xl font-semibold hover:bg-[#006528] transition-all"
              data-testid="new-expediente-btn">
              <FileSearch size={14} /> + {t("common.new")}
            </button>
          )}
        </div>
      </div>

      {dg && !compareMode && (
        <div className="text-xs text-slate-500 -mt-3" data-testid="om-dg-active-note">
          Filtro activo: <b className="text-slate-700">{DG_LABELS[dg]}</b> · KPIs recalculados sobre {totalActive + ganadas.length} expedientes.
        </div>
      )}

      {compareMode && (() => {
        const byDG = (list, dgVal) => (list || []).filter(x => (x.direccion_general || "").toUpperCase() === dgVal);
        const licTra = byDG(licitaciones, "TRATAMIENTO");
        const licSer = byDG(licitaciones, "SERVICIOS");
        const activasFn = ls => ls.filter(l => !["ganada","perdida","descartada"].includes(l.estado_crm));
        const pipelineFn = ls => Math.round(ls.reduce((s, l) => s + (l.importe_licitacion || 0), 0));
        const pondFn = ls => Math.round(ls.reduce((s, l) => s + ((l.importe_licitacion || 0) * (l.probabilidad_exito || 0) / 100), 0));
        const eurFmt = v => (v ? `${(Number(v)/1000).toFixed(0)}K€` : "0");
        const cm = [
          { key: "activos",  label: "En pipeline",           tra: activasFn(licTra).length, ser: activasFn(licSer).length },
          { key: "ganadas",  label: "Ganadas (win-rate)",    tra: licTra.filter(l => l.estado_crm === "ganada").length, ser: licSer.filter(l => l.estado_crm === "ganada").length },
          { key: "pipeline", label: "Pipeline total",        tra: pipelineFn(activasFn(licTra)), ser: pipelineFn(activasFn(licSer)), formatter: eurFmt },
          { key: "pond",     label: "Pipeline ponderado",    tra: pondFn(activasFn(licTra)), ser: pondFn(activasFn(licSer)), formatter: eurFmt, hint: "Valor × probabilidad de éxito" },
        ];
        return <DGCompareCard metrics={cm} onClose={() => setCompareMode(false)}/>;
      })()}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <KPICard title={t("dashboard.activeFilesKpi")} value={totalActive} subtitle={t("dashboard.inPipeline")} icon={FileSearch} color="bg-violet-600" onClick={() => navigate("/licitaciones")} />
        <KPICard title={t("dashboard.won")} value={ganadas.length} subtitle={t("dashboard.wonSub")} icon={Trophy} color="bg-green-600" onClick={() => navigate("/licitaciones")} />
        <KPICard title={t("dashboard.ongoingOffers")} value={myOffers.length} subtitle={t("dashboard.ongoingOffersSub")} icon={Briefcase} color="bg-amber-500" onClick={() => navigate("/offers")} />
        <KPICard title={t("dashboard.tcatApps")} value={22} subtitle={t("dashboard.tcatAppsSub")} icon={Layers} color="bg-blue-600" onClick={() => navigate("/catalogo-aplicaciones")} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Pipeline Kanban mini */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 font-['Outfit']">{t("dashboard.pipelineTitle")}</h3>
            <button onClick={() => navigate("/licitaciones")} className="text-xs text-[#007934] flex items-center gap-1 hover:underline">{t("common.actions")} <ChevronRight size={12}/></button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {PIPELINE_COLS.map(col => (
              <div key={col.key} className="text-center">
                <div className={`text-2xl font-bold font-['Outfit'] mb-1 ${pipelineMap[col.key].length > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                  {pipelineMap[col.key].length}
                </div>
                <div className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${col.color}`}>{col.label}</div>
              </div>
            ))}
          </div>
          {/* Recent licitaciones */}
          <div className="mt-4 space-y-1.5">
            {(licitaciones || []).filter(l => !["ganada","perdida","descartada"].includes(l.estado_crm)).slice(0,4).map(l => (
              <div key={l.id} onClick={() => navigate(`/licitaciones/${l.id}`)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-all group"
                data-testid={`pipeline-item-${l.id}`}>
                <FileSearch size={14} className="text-violet-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{l.titulo}</p>
                  <p className="text-xs text-gray-400">{l.municipio} · {l.tipo_servicio}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-bold text-gray-700">{(l.importe_licitacion || 0).toLocaleString("es-ES")}€</p>
                  <p className="text-[10px] text-violet-600 font-semibold">{l.probabilidad_exito || 0}%</p>
                </div>
              </div>
            ))}
            {ganadas.slice(0,1).map(l => (
              <div key={l.id} onClick={() => navigate(`/licitaciones/${l.id}`)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-green-50 border border-green-100 cursor-pointer transition-all group">
                <Trophy size={14} className="text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{l.titulo}</p>
                  <p className="text-xs text-gray-400">{l.municipio} · {l.tipo_servicio}</p>
                </div>
                <span className="text-[10px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">GANADA</span>
              </div>
            ))}
          </div>
        </div>

        {/* Accesos rápidos */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">{t("dashboard.quickActions")}</h3>
          <QuickAction icon={FileSearch}  label={t("layout.licitaciones")}      to="/licitaciones"            />
          <QuickAction icon={Briefcase}   label={t("layout.offers")}            to="/offers"                  />
          <QuickAction icon={MapPin}      label={t("layout.oraZaragoza")}       to="/ora-zaragoza"            />
          <QuickAction icon={Layers}      label={t("layout.appCatalog")}        to="/catalogo-aplicaciones"   />
          <QuickAction icon={BookOpen}    label={t("layout.manuals")}           to="/manuales"                />
        </div>
      </div>

      {/* Upcoming deadlines widget */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <UpcomingWidget />
        <PreComiteWidget />
      </div>

      {/* iter139 · Ranking Win Score */}
      <WinScoreRanking dgFilter={dg}/>

      {/* iter 130+ · Reporte mensual de categorías de rechazo (visible también a offer_manager por petición del director de ofertas) */}
      <CFORejectReport />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DASHBOARD IT_VALIDATOR
// ════════════════════════════════════════════════════════════════════════════
function DashboardITValidator({ stats, offers }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const pendingTIC = (offers || []).filter(o => o.status === "pending_it_validation");
  const pendingCFO = (offers || []).filter(o => o.status === "pending_cfo_approval");
  const projects = stats?.projects || {};

  return (
    <div className="space-y-6" data-testid="it-validator-dashboard">
      <div className="flex items-center justify-between">
        <Greeting name={user?.name} subtitle={t("dashboard.roles.itValidator")} />
        {pendingTIC.length > 0 && (
          <button onClick={() => navigate("/offers")}
            className="flex items-center gap-2 bg-amber-100 text-amber-800 text-sm px-4 py-2 rounded-full font-semibold hover:bg-amber-200 transition-all"
            data-testid="tic-pending-badge">
            <AlertTriangle size={14} /> {pendingTIC.length} · {t("dashboard.pendingTicTitle")}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <KPICard title={t("dashboard.pendingTicTitle")} value={pendingTIC.length} subtitle={t("dashboard.pendingTicSub")} icon={Shield} color="bg-amber-500" onClick={() => navigate("/offers")} />
        <KPICard title={t("dashboard.pendingCfoTitle")} value={pendingCFO.length} subtitle={t("dashboard.pendingCfoSub")} icon={CheckCircle} color="bg-blue-600" onClick={() => navigate("/offers")} />
        <KPICard title={t("dashboard.activeProjects")} value={projects.active || 0} subtitle={t("dashboard.activeProjectsSub", { n: projects.total || 0 })} icon={FolderKanban} color="bg-green-600" onClick={() => navigate("/projects")} />
        <KPICard title={t("dashboard.tcatCatalog")} value={22} subtitle={t("dashboard.tcatCatalogSub")} icon={Layers} color="bg-violet-600" onClick={() => navigate("/catalogo-aplicaciones")} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 font-['Outfit']">{t("dashboard.ticValidationQueue")}</h3>
            <button onClick={() => navigate("/offers")} className="text-xs text-[#007934] flex items-center gap-1 hover:underline">{t("common.actions")} <ChevronRight size={12}/></button>
          </div>
          {pendingTIC.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <CheckCircle size={32} className="text-green-400 mb-3" />
              <p className="text-sm font-medium text-gray-700">{t("delegado.empty.title")}</p>
              <p className="text-xs text-gray-400 mt-1">{t("delegado.empty.hint")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pendingTIC.map(o => (
                <div key={o.id} onClick={() => navigate(`/offers/${o.id}`)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-amber-100 bg-amber-50 hover:border-amber-300 cursor-pointer transition-all"
                  data-testid={`tic-pending-offer-${o.id}`}>
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Briefcase size={14} className="text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{o.title}</p>
                    <p className="text-xs text-gray-500">{o.municipio || "—"}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-[#007934]">{((o.total_capex || 0) + (o.total_opex || 0)).toLocaleString("es-ES")}€</p>
                    <p className="text-[10px] text-amber-700 font-semibold">{t("offers.validateTic").toUpperCase()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">{t("dashboard.quickActions")}</h3>
          <QuickAction icon={Briefcase}  label={t("layout.offers")}        to="/offers"                />
          <QuickAction icon={FolderKanban} label={t("layout.projects")}    to="/projects"              />
          <QuickAction icon={Layers}     label={t("layout.appCatalog")}    to="/catalogo-aplicaciones" />
          <QuickAction icon={ClipboardList} label={t("layout.checklists")} to="/checklists"            />
          <QuickAction icon={GitBranch}  label={t("layout.backlog")}       to="/backlog"               />
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DASHBOARD ADMIN / TECH — full dashboard (existing)
// ════════════════════════════════════════════════════════════════════════════
function DashboardFull({ stats, fetchStats }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [showAIWizard, setShowAIWizard] = useState(false);
  const { dg, setDg } = useDGFilter(user?.email);

  const { projects, budget, resources, sync, recent_projects, pending_approvals, workitems } = stats;
  const totalSpent = budget.total_spent || 0;
  const maxTotal = budget.max_total || 460000;
  const budgetPct = Math.round((totalSpent / maxTotal) * 100);

  const pieData = [
    { name: t("dashboard.projectState.active"),    value: projects.active },
    { name: t("dashboard.projectState.completed"), value: projects.completed },
    { name: t("dashboard.projectState.paused"),    value: Math.max(0, projects.total - projects.active - projects.completed - projects.blocked) },
    { name: t("dashboard.projectState.blocked"),   value: projects.blocked },
  ].filter(d => d.value > 0);

  const budgetData = [
    { name: t("dashboard.budgetCat.licenses"), presupuesto: budget.budget_licencias, ejecutado: budget.spent_licencias },
    { name: t("dashboard.budgetCat.external"), presupuesto: budget.budget_external,  ejecutado: budget.spent_external  },
    { name: t("dashboard.budgetCat.internal"), presupuesto: budget.budget_internal,  ejecutado: budget.spent_internal  },
  ];

  const priorityColor = { critica: "priority-critica", alta: "priority-alta", media: "priority-media", baja: "priority-baja" };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <Greeting name={user?.name} subtitle={t("dashboard.roles.admin")} />
        <div className="flex items-center gap-2">
          <DGFilter value={dg} onChange={setDg} size="sm" testidPrefix="admin-dg-filter"/>
          {pending_approvals > 0 && (
            <span className="bg-yellow-100 text-yellow-800 text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5" data-testid="pending-approvals-badge">
              <AlertTriangle size={12} />
              {pending_approvals} · {t("dashboard.pendingApproval")}
            </span>
          )}
          <AdminDelegateChip />
          <button onClick={fetchStats} className="p-2 text-gray-400 hover:text-gray-600 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-all" data-testid="refresh-btn">
            <RefreshCw size={16} />
          </button>
          <button
            onClick={() => setShowAIWizard(true)}
            data-testid="dashboard-ai-wizard-btn"
            className="flex items-center gap-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-semibold px-3 py-2 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm"
            title="Importar Oferta TIC desde Excel con Claude Sonnet 4.6"
          >
            <Sparkles size={12}/> Importar Excel con Pando
          </button>
        </div>
      </div>

      {/* iter 128: Delegate coverage panel (admin) */}
      <DelegateCoveragePanel />

      {/* T13 · Alerta de candidatos de conciliación de expedientes sin revisar
          (petición explícita del usuario, 13/10/2026): "para que no se
          acumulen sin que nadie los vea" */}
      <ConciliacionAlertBanner />

      {/* Manuales strip */}
      <div className="flex items-center justify-between bg-white border border-gray-100 rounded-xl px-4 py-2.5" data-testid="manual-download-banner">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <BookOpen size={14} className="text-[#007934]" />
          <span>{t("manuals.title")}</span>
          <span className="text-gray-300">·</span>
          <span className="text-xs text-gray-400">CFO · Bid Manager · Tender Specialist · IT Admin</span>
        </div>
        <a href="/manuales" className="flex items-center gap-1.5 text-[#007934] text-xs font-semibold hover:underline" data-testid="manual-download-link">
          <BookOpen size={12} /> {t("manuals.title")}
        </a>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger" data-testid="kpi-grid">
        <KPICard title={t("dashboard.activeProjects")} value={projects.active} subtitle={t("dashboard.activeProjectsSub", { n: projects.total })} icon={FolderKanban} color="bg-green-600" onClick={() => navigate("/projects")} />
        <KPICard title={t("dashboard.budgetExecuted")} value={totalSpent} subtitle={`${budgetPct}% de ${(maxTotal/1000).toFixed(0)}K€`} icon={PiggyBank} color="bg-blue-600" suffix="€" onClick={() => navigate("/budget")} />
        <KPICard title={t("layout.resources")} value={resources.active_roster ?? resources.total} subtitle={`En plantilla · ${resources.people_with_tasks ?? 0} con tareas asignadas`} icon={Users} color="bg-purple-600" onClick={() => navigate("/resources")} data-testid="dashboard-kpi-resources" />
        <KPICard title="WIs" value={workitems?.completed || 0} subtitle={`/ ${workitems?.total || 0}`} icon={CheckCircle} color="bg-teal-600" onClick={() => navigate("/backlog")} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5" data-testid="budget-overview-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 font-['Outfit']">Ejecución Presupuestaria 2026</h3>
            <button onClick={() => navigate("/budget")} className="text-xs text-[#007934] font-medium flex items-center gap-1 hover:underline">Ver detalle <ChevronRight size={12} /></button>
          </div>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1.5">
              <span className="text-gray-600">Total ejecutado</span>
              <span className="font-bold text-[#007934]">{totalSpent.toLocaleString("es-ES")}€ / {maxTotal.toLocaleString("es-ES")}€</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#007934] to-[#4CAF50] rounded-full transition-all duration-1000"
                style={{ width: `${Math.min(budgetPct, 100)}%` }} data-testid="budget-progress-bar" />
            </div>
            <p className="text-right text-xs text-gray-500 mt-1">{budgetPct}% ejecutado · {(maxTotal - totalSpent).toLocaleString("es-ES")}€ disponible</p>
          </div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={budgetData} barCategoryGap="25%">
                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={(v) => [`${v.toLocaleString("es-ES")}€`]} />
                <Bar dataKey="presupuesto" name="Asignado" fill="#e5e7eb" radius={[4,4,0,0]} />
                <Bar dataKey="ejecutado" name="Ejecutado" fill="#007934" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="project-status-chart">
          <h3 className="font-semibold text-gray-900 font-['Outfit'] mb-4">Estado Proyectos</h3>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" paddingAngle={3}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {pieData.map((entry, i) => (
              <div key={entry.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="text-gray-600">{entry.name}</span>
                </div>
                <span className="font-semibold text-gray-800">{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Projects + Sync */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5" data-testid="recent-projects-table">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 font-['Outfit']">Proyectos Recientes</h3>
            <button onClick={() => navigate("/projects")} className="text-xs text-[#007934] font-medium flex items-center gap-1 hover:underline">Ver todos <ChevronRight size={12} /></button>
          </div>
          <div className="space-y-2">
            {(recent_projects || []).map(p => (
              <div key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group">
                <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                  <FolderKanban size={14} className="text-[#007934]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate group-hover:text-[#007934]">{p.title}</p>
                  <p className="text-xs text-gray-400">{p.area} · {p.responsible_tech}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityColor[p.priority] || "priority-media"}`}>{p.priority}</span>
                  <div className="w-12 h-1.5 bg-gray-100 rounded-full">
                    <div className="h-full bg-[#007934] rounded-full" style={{ width: `${p.progress || 0}%` }} />
                  </div>
                  <span className="text-xs text-gray-500">{p.progress || 0}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="sync-status-card">
          <h3 className="font-semibold text-gray-900 font-['Outfit'] mb-4">Azure DevOps</h3>
          {sync ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${sync.status === "completed" ? "bg-green-500" : sync.status === "running" ? "bg-yellow-500 animate-pulse" : "bg-red-500"}`} />
                <span className="text-sm font-medium capitalize">{sync.status}</span>
              </div>
              <div className="text-xs text-gray-500 space-y-1">
                <p><span className="font-medium">Última sync:</span> {sync.started_at ? new Date(sync.started_at).toLocaleString("es-ES") : "N/A"}</p>
                <p><span className="font-medium">Items sincronizados:</span> {sync.items_synced || 0}</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <RefreshCw size={24} className="text-gray-300 mx-auto mb-2" />
              <p className="text-xs text-gray-500">Sin sincronización</p>
            </div>
          )}
          <div className="mt-4 p-3 bg-[#F0FDF4] rounded-lg text-xs text-[#007934]">
            <div className="flex items-center gap-1.5 mb-1"><Clock size={11} /><span className="font-medium">Sincronización automática</span></div>
            {/* P02 (roadmap-valoriza-revision-despliegue-2026-10-12, E13) ·
                el texto fijo "8:00, 14:00, 20:00" no coincidía con el cron
                real (interval cada 6h desde el arranque del servicio, sin
                horas fijas de reloj) — fuente única con Admin → Sync Health. */}
            <p className="text-green-600">Cada 6 horas desde el arranque del servicio (sin horas fijas de reloj)</p>
          </div>
          <button onClick={() => navigate("/devops")} className="mt-3 w-full text-xs text-center text-[#007934] font-medium hover:underline flex items-center justify-center gap-1">
            Gestionar sincronización <ChevronRight size={11} />
          </button>
        </div>
      </div>

      {/* Upcoming deadlines widget */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <UpcomingWidget />
        <PreComiteWidget />
      </div>

      {/* AI Usage widget — sólo visible para admin (el backend devuelve 403 al resto) */}
      {user?.role === "admin" && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <AIUsageWidget />
            <BudgetCoverageWidget />
          </div>
          {/* iter 128: CFO Priority Digest bar también para admin (visibilidad y testing) */}
          <CFODigestBar />
          {/* iter 136: patrón de rechazos + botón alerta a Pablo (accesible al admin) */}
          <CFORejectReport />
        </>
      )}
      <OfferAIWizardModal open={showAIWizard} onClose={() => setShowAIWizard(false)} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter 128 · Admin Delegate Chip + Coverage Panel
// ═══════════════════════════════════════════════════════════════════════════
function AdminDelegateChip() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [open, setOpen] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);  // pending row being rejected
  const [rejectReason, setRejectReason] = useState("");
  const [rejectCategory, setRejectCategory] = useState("insufficient_reason");

  const load = useCallback(() => {
    if (user?.role !== "admin") return;
    axios.get(`${API}/delegado/admin/pending-changes`)
      .then(r => setPending(r.data || []))
      .catch(() => setPending([]));
  }, [user]);
  useEffect(() => { load(); }, [load]);

  if (user?.role !== "admin" || pending.length === 0) return null;

  const doApprove = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/delegado/admin/pending-changes/${id}/approve`);
      window.dispatchEvent(new Event("coverage-changed"));   // iter 130
      load();
    } catch (e) {
      window.alert("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusyId(null); }
  };

  const doReject = async () => {
    if (!rejectFor) return;
    setBusyId(rejectFor.id);
    try {
      await axios.post(
        `${API}/delegado/admin/pending-changes/${rejectFor.id}/reject`,
        { reason: `[${rejectCategory}] ${rejectReason}`.trim() }
      );
      setRejectFor(null);
      setRejectReason("");
      setRejectCategory("insufficient_reason");
      load();
    } catch (e) {
      window.alert("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusyId(null); }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="admin-delegate-chip"
        className="bg-indigo-100 text-indigo-800 text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1.5 hover:bg-indigo-200 animate-pulse">
        <MapPin size={12}/>
        {pending.length} zona{pending.length === 1 ? "" : "s"} pendiente{pending.length === 1 ? "" : "s"}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-w-[92vw] bg-white rounded-xl border border-indigo-200 shadow-xl z-40" data-testid="admin-delegate-panel">          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-bold text-slate-700">Delegados · cambios de zona</span>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700 text-xs">✕</button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
            {pending.map(p => {
              const currentProvs = p.current?.provincias || [];
              const newProvs = p.proposed?.provincias || currentProvs;
              const added = newProvs.filter(x => !currentProvs.includes(x));
              const removed = currentProvs.filter(x => !newProvs.includes(x));
              return (
                <div key={p.id} className="p-3 text-xs" data-testid={`admin-pending-row-${p.id}`}>
                  <div className="font-bold text-slate-800">{p.nombre || p.email}</div>
                  <div className="text-slate-500 text-[10px]">
                    Solicitado {(p.requested_at || "").slice(0, 10)}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {added.map(a => (
                      <span key={a} className="bg-emerald-100 text-emerald-700 text-[10px] font-semibold px-1.5 py-0.5 rounded">+{a}</span>
                    ))}
                    {removed.map(r => (
                      <span key={r} className="bg-rose-100 text-rose-700 text-[10px] font-semibold px-1.5 py-0.5 rounded line-through">-{r}</span>
                    ))}
                    {!added.length && !removed.length && <span className="text-slate-400 italic">Sin cambios netos</span>}
                  </div>
                  {p.change_reason && (
                    <div className="mt-1.5 text-[11px] text-slate-600 bg-slate-50 rounded px-2 py-1 italic">
                      &ldquo;{p.change_reason}&rdquo;
                    </div>
                  )}
                  <div className="flex gap-1.5 mt-2">
                    <button
                      onClick={() => doApprove(p.id)}
                      disabled={busyId === p.id}
                      data-testid={`admin-approve-${p.id}`}
                      className="flex-1 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded font-semibold disabled:opacity-50">
                      ✓ Aprobar
                    </button>
                    <button
                      onClick={() => setRejectFor(p)}
                      disabled={busyId === p.id}
                      data-testid={`admin-reject-${p.id}`}
                      className="flex-1 text-[10px] bg-rose-600 hover:bg-rose-700 text-white px-2 py-1 rounded font-semibold disabled:opacity-50">
                      ✗ Rechazar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* iter 129 · Reject modal estructurado (reemplaza window.prompt) */}
      {rejectFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRejectFor(null)} data-testid="admin-reject-modal">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-rose-600 to-red-500 text-white px-5 py-3 rounded-t-2xl">
              <h3 className="font-bold text-sm">Rechazar cambio de zona</h3>
              <p className="text-[11px] opacity-90 mt-0.5">{rejectFor.nombre || rejectFor.email}</p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1.5">Categoría del rechazo</label>
                <div className="space-y-1">
                  {[
                    { key: "insufficient_reason", label: "Motivo insuficiente", desc: "El justificante no argumenta el cambio" },
                    { key: "conflict_with_other", label: "Solapamiento con otro delegado", desc: "La provincia ya cubierta por otro" },
                    { key: "workload_excessive", label: "Carga excesiva", desc: "Sumaría más de N provincias" },
                    { key: "temporary_freeze",   label: "Freeze temporal",   desc: "No es momento de tocar zonas" },
                    { key: "other",              label: "Otro",              desc: "Detallar en comentarios" },
                  ].map(cat => (
                    <label
                      key={cat.key}
                      className={`text-[11px] cursor-pointer border rounded-lg px-2 py-1.5 block transition ${
                        rejectCategory === cat.key
                          ? "bg-rose-100 border-rose-500 text-rose-900"
                          : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                      }`}>
                      <input type="radio" name="admin-reject-cat"
                        checked={rejectCategory === cat.key}
                        onChange={() => setRejectCategory(cat.key)}
                        data-testid={`admin-reject-cat-${cat.key}`}
                        className="mr-1.5 accent-rose-600"/>
                      <b>{cat.label}</b>
                      <div className="text-[10px] opacity-70 ml-4">{cat.desc}</div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Comentario adicional</label>
                <textarea
                  rows="3"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  data-testid="admin-reject-reason-input"
                  className="w-full text-xs border border-slate-200 rounded p-2 focus:outline-none focus:border-rose-500"
                  placeholder="Ej. La provincia de Madrid está reservada al delegado nacional"/>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setRejectFor(null)}
                  className="flex-1 text-xs border border-slate-200 text-slate-600 rounded-lg py-2 hover:bg-slate-50">
                  Cancelar
                </button>
                <button
                  onClick={doReject}
                  disabled={!rejectCategory || busyId === rejectFor.id}
                  data-testid="admin-reject-confirm"
                  className="flex-1 text-xs bg-rose-600 hover:bg-rose-700 text-white rounded-lg py-2 font-semibold disabled:opacity-50">
                  Confirmar rechazo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════
// T13 · Alerta de candidatos de conciliación de expedientes (PLACSP↔TED↔
// Euskadi) sin revisar. Sólo informa y enlaza a Competitive Intel — nunca
// decide ni fusiona nada por sí misma.
// ══════════════════════════════════════════════════════════════════════════
function ConciliacionAlertBanner() {
  const navigate = useNavigate();
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/competitive-intel/conciliacion/candidatos`)
      .then(r => setCount((r.data?.candidatos || []).length))
      .catch(() => setCount(null));
  }, []);

  if (!count) return null;

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="conciliacion-alert-banner">
      <div className="flex items-center gap-2">
        <Link2 size={16} className="text-indigo-700"/>
        <span className="text-sm font-bold text-indigo-900">{count} candidatos de conciliación entre fuentes sin revisar</span>
        <span className="text-[11px] text-indigo-600">PLACSP · TED · Euskadi — ¿es el mismo expediente real?</span>
      </div>
      <button
        onClick={() => navigate("/tender360?tab=competitive")}
        data-testid="conciliacion-alert-ir-btn"
        className="text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-full font-semibold flex items-center gap-1">
        Revisar ahora <ArrowRight size={11}/>
      </button>
    </div>
  );
}

function DelegateCoveragePanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [selected, setSelected] = useState({});   // {province: delegate_email}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (user?.role !== "admin") return;
    axios.get(`${API}/delegado/admin/coverage-alerts`)
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, [user]);
  useEffect(() => { load(); }, [load]);

  if (user?.role !== "admin" || !data) return null;
  const hasAny = data.uncovered_count > 0 || data.inactive_count > 0;
  if (!hasAny) return null;

  const loadSuggestions = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/delegado/admin/coverage-suggest`);
      setSuggestions(r.data);
      // Preselect all suggestions that have delegate_email
      const preset = {};
      for (const s of r.data.suggestions || []) {
        if (s.delegate_email) preset[s.province] = s.delegate_email;
      }
      setSelected(preset);
    } catch (e) {
      window.alert("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const applyAll = async () => {
    const assignments = Object.entries(selected).map(([province, delegate_email]) => ({ province, delegate_email }));
    if (!assignments.length) return;
    if (!window.confirm(`Aplicar ${assignments.length} asignaciones de provincia?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/delegado/admin/coverage-apply`, { assignments });
      window.alert(`${r.data.applied} aplicadas · ${r.data.skipped.length} omitidas`);
      // iter 130: notifica a otras vistas (DelegadosPanel > CoverageMap) para refrescar
      window.dispatchEvent(new Event("coverage-changed"));
      setSuggestions(null);
      setSelected({});
      load();
    } catch (e) {
      window.alert("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="delegate-coverage-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-700"/>
          <span className="text-sm font-bold text-amber-900">Alertas de cobertura delegados</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-amber-800 bg-white px-2 py-0.5 rounded-full border border-amber-200 font-mono" data-testid="coverage-pct">
            {data.coverage_pct}% cobertura ({data.covered_provinces.length}/{data.covered_provinces.length + data.uncovered_count})
          </span>
          {data.uncovered_count > 0 && !suggestions && (
            <button
              onClick={loadSuggestions}
              disabled={busy}
              data-testid="coverage-suggest-btn"
              className="text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-full font-semibold disabled:opacity-50 flex items-center gap-1">
              {busy ? <Loader2 className="animate-spin" size={10}/> : <Sparkles size={10}/>}
              Sugerir asignaciones Pando
            </button>
          )}
        </div>
      </div>

      {/* Sub-cards uncovered / inactive */}
      <div className="grid md:grid-cols-2 gap-3 mt-2">
        {data.uncovered_count > 0 && (
          <div className="bg-white rounded-lg border border-amber-100 p-3" data-testid="coverage-uncovered">
            <div className="text-[11px] font-bold text-amber-900 uppercase tracking-wide mb-1">
              {data.uncovered_count} provincias sin delegado activo
            </div>
            <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
              {data.uncovered_provinces.map(p => (
                <span key={p} className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-semibold">{p}</span>
              ))}
            </div>
          </div>
        )}
        {data.inactive_count > 0 && (
          <div className="bg-white rounded-lg border border-rose-100 p-3" data-testid="coverage-inactive">
            <div className="text-[11px] font-bold text-rose-900 uppercase tracking-wide mb-1">
              {data.inactive_count} delegados inactivos &gt;14 días
            </div>
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {data.inactive_delegates.map(d => (
                <div key={d.email} className="text-[11px] flex justify-between items-center" data-testid={`inactive-${d.email}`}>
                  <span className="font-semibold text-slate-700 truncate">{d.nombre}</span>
                  <span className="text-rose-700 font-mono text-[10px]">
                    {d.days_since != null ? `${d.days_since}d` : "sin actividad"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* iter 129 · Sugerencias de asignación */}
      {suggestions && (
        <div className="mt-3 bg-white rounded-lg border border-indigo-200 p-3" data-testid="coverage-suggestions">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold text-indigo-800 flex items-center gap-1">
              <Sparkles size={12}/> {suggestions.with_suggestion} sugerencias sobre {suggestions.uncovered_count} provincias sin cubrir
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setSuggestions(null); setSelected({}); }}
                className="text-[11px] border border-slate-200 text-slate-600 px-2 py-1 rounded hover:bg-slate-50">
                Descartar
              </button>
              <button
                onClick={applyAll}
                disabled={busy || Object.keys(selected).length === 0}
                data-testid="coverage-apply-btn"
                className="text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded font-semibold disabled:opacity-50">
                Aplicar {Object.keys(selected).length}
              </button>
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto space-y-1">
            {suggestions.suggestions.map(s => (
              <label
                key={s.province}
                className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded cursor-pointer ${
                  selected[s.province] ? "bg-emerald-50 border border-emerald-200" : "hover:bg-slate-50"
                } ${!s.delegate_email ? "opacity-60" : ""}`}
                data-testid={`suggest-row-${s.province}`}>
                <input
                  type="checkbox"
                  disabled={!s.delegate_email}
                  checked={!!selected[s.province]}
                  onChange={() => setSelected(prev => {
                    const next = { ...prev };
                    if (next[s.province]) delete next[s.province];
                    else if (s.delegate_email) next[s.province] = s.delegate_email;
                    return next;
                  })}
                  className="accent-emerald-600"/>
                <span className="font-bold text-slate-800 w-20">{s.province}</span>
                <span className="text-slate-500 text-[10px] w-24">{s.ccaa || "—"}</span>
                <span className="flex-1 text-slate-600">
                  {s.delegate_email ? (
                    <>→ <b>{s.delegate_name}</b> ({s.current_load} prov.)</>
                  ) : (
                    <span className="italic">{s.reason}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD — routes by role
// ════════════════════════════════════════════════════════════════════════════
export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [offers, setOffers] = useState([]);
  const [licitaciones, setLicitaciones] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, offersRes, licsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/offers`).catch(() => ({ data: [] })),
        axios.get(`${API}/licitaciones`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setOffers(offersRes.data || []);
      setLicitaciones(licsRes.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        {[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-xl loading-shimmer" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-72 rounded-xl loading-shimmer" />
        <div className="h-72 rounded-xl loading-shimmer" />
      </div>
    </div>
  );

  const role = user?.role || "tech";

  if (role === "cfo") return <DashboardCFO stats={stats} offers={offers} licitaciones={licitaciones} />;
  if (role === "offer_manager") return <DashboardOfferManager stats={stats} offers={offers} licitaciones={licitaciones} />;
  if (role === "it_validator") return <DashboardITValidator stats={stats} offers={offers} />;
  return <DashboardFull stats={stats} fetchStats={fetchStats} />;
}

// ══════════════════════════════════════════════════════════════════════════
// Iter139 · Ranking Win Score · top 10 oportunidades por probabilidad de ganar
// ══════════════════════════════════════════════════════════════════════════
function WinScoreRanking({ dgFilter }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        estado: "PUBLICADA",
        limit: "300",
        page: "1",
      });
      if (dgFilter) params.set("direccion_general", dgFilter);
      const r = await axios.get(`${API}/mercado?${params.toString()}`);
      const scored = (r.data?.items || [])
        .filter(m => typeof m.ai_win_score === "number")
        .sort((a, b) => b.ai_win_score - a.ai_win_score)
        .slice(0, 10);
      setItems(scored);
    } catch { /* no-op */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dgFilter]);

  const fmtEur = v => {
    const n = Number(v) || 0;
    return n >= 1_000_000 ? `${(n/1_000_000).toFixed(1)} M€`
        : n >= 1000       ? `${(n/1000).toFixed(0)} K€`
        : n === 0         ? "—"
                          : `${Math.round(n)} €`;
  };

  return (
    <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-5" data-testid="win-score-ranking">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-bold text-emerald-800 uppercase tracking-widest">Top oportunidades</div>
          <h3 className="font-semibold text-slate-900 text-base flex items-center gap-2">
            <TrendingUp size={16} className="text-emerald-600"/> Ranking Win Score
          </h3>
          <p className="text-[11px] text-slate-500">Oportunidades PUBLICADAS ordenadas por probabilidad de ganar (Pando + noticias).</p>
        </div>
        <button onClick={load} data-testid="win-score-ranking-refresh"
          className="text-xs border border-slate-200 hover:bg-slate-50 rounded-lg px-2.5 py-1">Refrescar</button>
      </div>
      {loading ? (
        <div className="text-xs text-slate-400 py-4 text-center">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-400 py-6 text-center">
          Sin oportunidades scored. Ejecuta el <b>Score Pando</b> desde /admin.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((m, i) => {
            const s = m.ai_win_score;
            const color = s >= 70 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                       : s >= 45 ? "text-amber-700 bg-amber-50 border-amber-200"
                       :           "text-rose-700 bg-rose-50 border-rose-200";
            return (
              <div key={m.id} className="flex items-center gap-2 text-xs border border-slate-100 rounded-lg px-2 py-1.5 hover:bg-slate-50 transition" data-testid={`win-rank-${i+1}`}>
                <div className="text-[10px] font-bold w-4 text-slate-400">#{i+1}</div>
                <div className={`font-mono text-[10px] font-bold w-11 text-center rounded-full border ${color} py-0.5`}>
                  {s}%
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 truncate" title={m.titulo_ia || m.objeto_contrato}>
                    {(m.titulo_ia || m.objeto_contrato || "—").slice(0, 80)}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {m.cliente || "—"} · {m.provincia || m.comunidad_autonoma || "—"} · {fmtEur(m.presupuesto)}
                  </div>
                </div>
                {m.url_externa && (
                  <a href={m.url_externa} target="_blank" rel="noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                    onClick={e => e.stopPropagation()}
                    data-testid={`win-rank-${i+1}-link`}
                    title="Abrir ficha oficial">
                    <ExternalLink size={12}/>
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
